<template>
  <div>
    <!-- Loading State -->
    <div v-if="isLoading" class="dashboard-loading">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p class="loading-text">Loading business details...</p>
      </div>
    </div>

    <!-- Dashboard Content -->
    <div v-else-if="businessDetails" class="dashboard-content">
      <!-- Header Section -->
      <div class="dashboard-header">
        <div class="header-content">
          <div class="business-logo">
            <img
              v-if="businessDetails.logo"
              :src="businessDetails.logo"
              :alt="businessDetails.name"
              class="logo-image"
            />
            <div v-else class="logo-placeholder">
              <feather-icon icon="BuildingIcon" size="48" />
            </div>
          </div>
          <div class="business-info">
            <h1 class="business-name">
              {{ businessDetails.name }}
            </h1>
            <p class="business-tagline">{{ businessDetails.name }}</p>
            <div class="business-meta">
              <span class="meta-item">
                <feather-icon icon="MapPinIcon" size="16" />
                <div>{{ businessDetails.address }}<br />{{ businessDetails.province }}</div>
              </span>
              <span class="meta-item">
                <feather-icon icon="CalendarIcon" size="16" />
                {{ formatDate(businessDetails.createdDate) }}
              </span>
            </div>
          </div>
          <div class="header-actions">
            <!-- Watch Now Button -->
            <b-button variant="outline-success" size="lg" class="mr-3" @click="openWatchVideo">
              <feather-icon icon="PlayIcon" size="18" class="mr-2" />
              Watch Now
            </b-button>

            <b-button variant="primary" size="lg" @click="openUpdateModal" class="update-btn">
              <feather-icon icon="EditIcon" size="18" class="mr-2" />
              Update Business Details
            </b-button>
          </div>
        </div>
      </div>

      <!-- Business Details Cards -->
      <div class="dashboard-grid">
        <!-- Contact Information -->
        <b-card class="info-card contact-card">
          <template #header>
            <div class="card-header">
              <feather-icon icon="PhoneIcon" size="20" class="card-icon" />
              <h5 class="card-title">Contact Information</h5>
            </div>
          </template>
          <div class="contact-details">
            <div class="contact-item">
              <feather-icon icon="PhoneIcon" size="16" class="contact-icon" />
              <div class="contact-text">
                <label>Phone</label>
                <span>{{ businessDetails.phone || 'Not provided' }}</span>
              </div>
            </div>
            <div class="contact-item">
              <feather-icon icon="MailIcon" size="16" class="contact-icon" />
              <div class="contact-text">
                <label>Email</label>
                <span>{{ businessDetails.email || 'Not provided' }}</span>
              </div>
            </div>
            <div class="contact-item">
              <feather-icon icon="GlobeIcon" size="16" class="contact-icon" />
              <div class="contact-text">
                <label>Website</label>
                <span>{{ businessDetails.website || 'Not provided' }}</span>
              </div>
            </div>
          </div>
        </b-card>

        <!-- Business Information -->
        <b-card class="info-card business-card">
          <template #header>
            <div class="card-header">
              <feather-icon icon="BriefcaseIcon" size="20" class="card-icon" />
              <h5 class="card-title">Business Information</h5>
            </div>
          </template>
          <div class="business-details">
            <div class="detail-item">
              <label>Name</label>
              <span>{{ businessDetails.name || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <label>NTN</label>
              <span>{{ businessDetails.ntn || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <label>CNIC</label>
              <span>{{ businessDetails.cnic || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <label>STRN</label>
              <span>{{ businessDetails.strn || businessDetails.gstNumber || businessDetails.gstNo || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <label>Province</label>
              <span>{{ businessDetails.province || 'N/A' }}</span>
            </div>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Error State -->
    <div v-else class="dashboard-error">
      <div class="error-content">
        <feather-icon icon="AlertTriangleIcon" size="64" class="error-icon" />
        <h3>Failed to load business details</h3>
        <p>Please try refreshing the page or contact support if the problem persists.</p>
        <b-button variant="primary" @click="retryLoading">
          <feather-icon icon="RefreshCwIcon" size="18" class="mr-2" />
          Retry
        </b-button>
      </div>
    </div>

    <!-- Update Business Modal -->
    <b-modal
      v-model="showUpdateModal"
      title="Update Business Details"
      size="lg"
      :hide-footer="true"
      class="update-modal"
    >
      <div v-if="isUpdating" class="modal-loading">
        <div class="loading-spinner">
          <div class="spinner"></div>
          <p>Updating business details...</p>
        </div>
      </div>

      <validation-observer v-else>
        <b-form
          slot-scope="{ validate }"
          @submit.prevent="validate().then(handleUpdateBusiness).catch(catchUpdateBusiness)"
          class="update-form"
        >
          <!-- Row 1: Business Name | NTN Number -->
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Business Name *" label-for="businessName">
                <b-form-input
                  id="businessName"
                  v-model="updateForm.businessName"
                  :state="validateBusinessName"
                  placeholder="Enter business name"
                  required
                />
                <b-form-invalid-feedback :state="validateBusinessName">
                  Business name is required
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="`NTN Number ${hasCnic ? '' : '*'}`" label-for="ntnNumber">
                <b-form-input
                  id="ntnNumber"
                  v-model="updateForm.ntn"
                  :state="validateNtn"
                  placeholder="Enter NTN number (F123456-7 or 1234567)"
                  v-mask="ntnMask"
                  @input="handleNtnInput"
                  @blur="handleNtnBlur"
                />
                <b-form-invalid-feedback :state="validateNtn">
                  {{
                    hasCnic
                      ? 'NTN is optional when CNIC is provided'
                      : 'NTN is required when CNIC is not provided'
                  }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 2: Phone | Email -->
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group label="Phone" label-for="phone">
                <b-form-input
                  id="phone"
                  v-model="updateForm.phone"
                  placeholder="Enter phone number (+92##########)"
                  v-mask="'+92##########'"
                  required
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <form-input v-model="updateForm.email" label="Email" name="Email" rules="required" />
            </b-col>
          </b-row>

          <!-- Row 3: Website | Province -->
          <b-row>
            <b-col cols="12" md="6">
              <form-input
                v-model="updateForm.website"
                label="Website"
                name="Website"
                placeholder="Enter website URL"
              />
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group label="Province" label-for="province">
                <v-select
                  id="province"
                  v-model="province"
                  :options="provinceOptions"
                  placeholder="Select province"
                  label="text"
                  :reduce="(option) => option.value"
                  :clearable="false"
                />
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6">
              <b-form-group label="City *" label-for="city">
                <v-select
                  id="city"
                  v-model="city"
                  :options="cityOptions"
                  placeholder="Select city"
                  label="text"
                  :reduce="(option) => option.value"
                  :clearable="false"
                />
                <b-form-invalid-feedback :state="validateCity">
                  City is required
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <b-col cols="12">
              <b-form-group label="Address *" label-for="address">
                <b-form-textarea
                  id="address"
                  v-model="updateForm.address_complete"
                  :state="validateAddress"
                  placeholder="Enter business address"
                  label="City"
                  rows="3"
                />
                <b-form-invalid-feedback :state="validateAddress">
                  Address is required
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group :label="`CNIC ${hasNtn ? '' : '*'}`" label-for="cnic">
                <b-form-input
                  id="cnic"
                  v-model="updateForm.cnic"
                  :state="validateCnic"
                  placeholder="Enter CNIC number (13 digits)"
                  v-mask="'#####-#######-#'"
                />
                <b-form-invalid-feedback :state="validateCnic">
                  {{
                    hasNtn
                      ? 'CNIC is optional when NTN is provided'
                      : 'CNIC is required when NTN is not provided'
                  }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
            <b-col cols="12" md="6">
              <b-form-group label="STRN Number" label-for="strn">
                <b-form-input
                  id="strn"
                  v-model="updateForm.strn"
                  placeholder="Enter STRN number"
                  v-mask="'##-##-####-###-##'"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 5: Staging Token | Live Token -->
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group
                :label="`Staging Token ${updateForm.integrationMode === 'Staging' ? '*' : ''}`"
                label-for="stagingToken"
              >
                <b-form-input
                  id="stagingToken"
                  v-model="updateForm.stagingToken"
                  :state="validateStagingToken"
                  placeholder="Enter staging token"
                />
                <b-form-invalid-feedback :state="validateStagingToken">
                  Staging token is required when Integration Mode is Staging
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <b-col cols="12" md="6" style="padding-top: 10px">
              <b-form-group label="Integration Mode *" label-for="integrationMode">
                <v-select
                  id="integrationMode"
                  v-model="updateForm.integrationMode"
                  :options="integrationModeOptions"
                  placeholder="Select integration mode"
                  label="text"
                  :reduce="(option) => option.value"
                  :clearable="false"
                  :state="validateIntegrationMode"
                />
                <b-form-invalid-feedback :state="validateIntegrationMode">
                  Integration mode is required
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Row 6: Live Token -->
          <b-row>
            <b-col cols="12" md="6">
              <b-form-group
                :label="`Live Token ${updateForm.integrationMode === 'Live' ? '*' : ''}`"
                label-for="liveToken"
              >
                <b-form-input
                  id="liveToken"
                  v-model="updateForm.liveToken"
                  :state="validateLiveToken"
                  placeholder="Enter live token"
                />
                <b-form-invalid-feedback :state="validateLiveToken">
                  Live token is required when Integration Mode is Live
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="modal-actions">
            <b-button variant="secondary" @click="closeUpdateModal" :disabled="isUpdating">
              Cancel
            </b-button>
            <b-button
              type="submit"
              variant="primary"
              :disabled="!isFormValid || isUpdating"
              class="ml-2"
            >
              <feather-icon v-if="isUpdating" icon="LoaderIcon" size="16" class="spinning mr-2" />
              <feather-icon v-else icon="SaveIcon" size="16" class="mr-2" />
              {{ isUpdating ? 'Updating...' : 'Update Business' }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </b-modal>
  </div>
</template>

<script>
import FormInput from '@/views/apps/fbr-invoice/components/form/FormInput.vue';
import { useToast } from 'vue-toastification/composition';
import BusinessStoreModule from './business/BusinessStoreModule';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import store from '@/store';
import { onUnmounted, ref, reactive, computed, watch } from '@vue/composition-api';
import { APIService } from '@/utils/api';
import {
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BFormTextarea,
  BFormInvalidFeedback,
  BModal,
  BRow,
  BBadge,
  BForm,
} from 'bootstrap-vue';
import vSelect from 'vue-select';

import { ValidationProvider, ValidationObserver } from 'vee-validate';
import { VueMaskDirective } from 'v-mask';

import { CITIES } from '../utils/constants';

export default {
  name: 'FbrInvoiceDashboard',
  directives: {
    mask: VueMaskDirective,
  },
  components: {
    ToastificationContent,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BFormTextarea,
    BFormInvalidFeedback,
    BModal,
    BRow,
    BBadge,
    ValidationProvider,
    ValidationObserver,
    FormInput,
    BForm,
    vSelect,
  },
  setup() {
    const toast = useToast();

    const BUSINESS_STORE_MODULE_NAME = 'business';

    // Register module
    if (!store.hasModule(BUSINESS_STORE_MODULE_NAME))
      store.registerModule(BUSINESS_STORE_MODULE_NAME, BusinessStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(BUSINESS_STORE_MODULE_NAME))
        store.unregisterModule(BUSINESS_STORE_MODULE_NAME);
    });

    // Reactive state
    const isLoading = ref(true);
    const businessDetails = ref(null);
    const showUpdateModal = ref(false);
    const isUpdating = ref(false);

    // Update form data
    const updateForm = ref({
      id: 0,
      businessName: '',
      ntn: '',
      phone: '',
      email: '',
      website: '',
      province: '',
      city: '',
      address_complete: '',
      cnic: '',
      strn: '',
      stagingToken: '',
      liveToken: '',
      integrationMode: '',
      logo: '',
      createdDate: '',
      updatedDate: '',
      isDeleted: false,
    });

    // Integration mode options
    const integrationModeOptions = [
      { value: 'Live', text: 'Live' },
      { value: 'Staging', text: 'Staging' },
    ];

    // Province options
    const provinceOptions = [
      { value: 'Punjab', text: 'Punjab' },
      { value: 'Sindh', text: 'Sindh' },
      { value: 'Khyber Pakhtunkhwa', text: 'Khyber Pakhtunkhwa' },
      { value: 'Balochistan', text: 'Balochistan' },
      { value: 'Gilgit-Baltistan', text: 'Gilgit-Baltistan' },
    ];
    const province = ref('');

    const cityOptions = computed(() =>
      CITIES.filter((city) => city.province === updateForm.value.province).map((city) => ({
        value: city.value,
        text: city.name,
      }))
    );
    const city = ref('');

    watch(province, (newVal) => {
      updateForm.value.city = '';
      updateForm.value.province = newVal;
    });

    watch(city, (newVal) => {
      updateForm.value.city = newVal;
    });

    // Video link for Watch Now button
    const dashboardVideoLink = 'https://cdn.cloudious.net/file-1756985312887-228624732.mp4';

    // Watch video function
    const openWatchVideo = () => {
      window.open(dashboardVideoLink, '_blank');
    };

    // Dynamic NTN mask based on input
    const ntnMask = computed(() => {
      const ntnValue = updateForm.value.ntn || '';
      // If starts with alphabet, use full format (A123456-7)
      if (ntnValue.length > 0 && /^[A-Za-z]/.test(ntnValue)) {
        return 'A######-#';
      }
      // If starts with digit or empty, use 7 digits only
      return '#######';
    });

    // Validation computed properties
    const validateBusinessName = computed(() => {
      return updateForm.value.businessName.length > 0;
    });

    const validateNtn = computed(() => {
      const ntnValue = (updateForm.value.ntn || '').toString().trim();
      const cnicValue = (updateForm.value.cnic || '').toString().trim();

      // Check if CNIC is filled with proper format (13 digits with dashes)
      const hasProperCnic = cnicValue.length > 0 && /^\d{5}-\d{7}-\d{1}$/.test(cnicValue);

      // If CNIC is properly filled, NTN is optional
      if (hasProperCnic) return true;

      // If CNIC is not properly filled, NTN is mandatory and must be properly formatted
      if (ntnValue.length === 0) return false;

      // If starts with letter, should be in format A123456-7 (9 characters total)
      if (/^[A-Za-z]/.test(ntnValue)) {
        return /^[A-Za-z]\d{6,7}(-\d{1})?$/.test(ntnValue);
      }

      // If starts with digit, should be exactly 7 digits
      if (/^\d/.test(ntnValue)) {
        return /^\d{7}$/.test(ntnValue);
      }

      return false;
    });

    const validateCity = computed(() => {
      return (updateForm.value.city || '').toString().trim().length > 0;
    });

    const validateAddress = computed(() => {
      return (updateForm.value.address_complete || '').toString().trim().length > 0;
    });

    const validateCnic = computed(() => {
      // CNIC is mandatory by default, but if NTN is properly filled, CNIC becomes optional
      const hasValidNtn = hasNtn.value; // Use the strict validation for NTN
      const hasCnic = (updateForm.value.cnic || '').toString().trim().length > 0;

      // If NTN is properly filled, CNIC is optional (show as valid)
      if (hasValidNtn) return true;

      // If NTN is not properly filled, CNIC must be filled
      return hasCnic;
    });

    const isFormValid = computed(() => {
      // Ensure at least one of CNIC or NTN is properly filled
      const hasValidNtn = hasNtn.value;
      const hasCnic = (updateForm.value.cnic || '').toString().trim().length > 0;
      const hasEitherCnicOrNtn = hasValidNtn || hasCnic;

      return (
        validateBusinessName.value &&
        validateCity.value &&
        validateAddress.value &&
        hasEitherCnicOrNtn &&
        validateIntegrationMode.value &&
        validateStagingToken.value &&
        validateLiveToken.value
      );
    });

    const hasNtn = computed(() => {
      const ntnValue = (updateForm.value.ntn || '').toString().trim();

      // NTN should be at least 7 digits or properly formatted with letter
      if (ntnValue.length === 0) return false;

      // If starts with letter, should be in format A123456-7 (9 characters total)
      if (/^[A-Za-z]/.test(ntnValue)) {
        return /^[A-Za-z]\d{6,7}(-\d{1})?$/.test(ntnValue);
      }

      // If starts with digit, should be exactly 7 digits
      if (/^\d/.test(ntnValue)) {
        return /^\d{7}$/.test(ntnValue);
      }

      return false;
    });

    const hasCnic = computed(() => {
      return (updateForm.value.cnic || '').toString().trim().length > 0;
    });

    const validateIntegrationMode = computed(() => {
      return (updateForm.value.integrationMode || '').toString().trim().length > 0;
    });

    const validateStagingToken = computed(() => {
      // Required only when Integration Mode is 'Staging'
      if ((updateForm.value.integrationMode || '').toString().trim() === 'Staging') {
        return (updateForm.value.stagingToken || '').toString().trim().length > 0;
      }
      return true;
    });

    const validateLiveToken = computed(() => {
      // Required only when Integration Mode is 'Live'
      if ((updateForm.value.integrationMode || '').toString().trim() === 'Live') {
        return (updateForm.value.liveToken || '').toString().trim().length > 0;
      }
      return true;
    });

    // Load business details
    const loadBusinessDetails = () => {
      isLoading.value = true;
      store
        .dispatch('business/fetchBusinessDetails')
        .then((response) => {
          businessDetails.value = response;

          // Initialize update form with current values
          Object.assign(updateForm.value, {
            id: response.id || 0,
            businessName: response.name || response.businessName || '',
            ntn: response.ntn || '',
            phone: response.phone || '',
            email: response.email || '',
            website: response.website || '',
            province: response.province || '',
            city: response.address || '',
            address_complete: response.address_complete || '',
            cnic: response.cnic || '',
            strn: response.strn || response.gstNumber || response.gstNo || '',
            stagingToken: response.stagingToken || '',
            liveToken: response.liveToken || '',
            integrationMode: response.integrationMode || '',
            logo: response.logo || '',
            createdDate: response.createdDate || '',
            updatedDate: response.updatedDate || '',
            isDeleted: response.isDeleted || false,
          });
          province.value = response.province || '';
          city.value = response.address || '';
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
              text: error.message || 'Failed to load business details',
            },
          });
        })
        .finally(() => {
          isLoading.value = false;
        });
    };

    // Initial load
    loadBusinessDetails();

    // Modal handlers
    const openUpdateModal = () => {
      showUpdateModal.value = true;
    };

    const closeUpdateModal = () => {
      showUpdateModal.value = false;
      // Reset form to current values
      if (businessDetails.value) {
        Object.assign(updateForm.value, {
          id: 0,
          businessName: businessDetails.value.name || businessDetails.value.businessName || '',
          ntn: businessDetails.value.ntn || '',
          phone: businessDetails.value.phone || '',
          email: businessDetails.value.email || '',
          website: businessDetails.value.website || '',
          address: businessDetails.value.address || '',
          province: businessDetails.value.province || '',
          cnic: businessDetails.value.cnic || '',
          strn: businessDetails.value.strn || businessDetails.value.gstNumber || businessDetails.value.gstNo || '',
          stagingToken: businessDetails.value.stagingToken || '',
          liveToken: businessDetails.value.liveToken || '',
          integrationMode: businessDetails.value.integrationMode || '',
          logo: businessDetails.value.logo || '',
          createdDate: businessDetails.value.createdDate || '',
          updatedDate: businessDetails.value.updatedDate || '',
          isDeleted: businessDetails.value.isDeleted || false,
        });
        console.log(updateForm.value);
      }
    };

    // Update business handler
    const handleUpdateBusiness = async () => {
      console.log('=== handleUpdateBusiness called');
      console.log('=== updateForm data:', updateForm.value);
      isUpdating.value = true;
      try {
        // Get userID and businessID from store
        const userID = store.state.user?.userID || store.state.userID || 1;
        const businessID = store.state.user?.businessID || store.state.businessID || 1;

        // Prepare payload for updating business using the Save API endpoint
        const updatePayload = {
          id: updateForm.value.id,
          businessID: businessID,
          name: updateForm.value.businessName,
          logo: updateForm.value.logo || '',
          address_complete: updateForm.value.address_complete,
          phone: updateForm.value.phone,
          email: updateForm.value.email,
          website: updateForm.value.website,
          stagingToken: updateForm.value.stagingToken,
          liveToken: updateForm.value.liveToken,
          integrationMode: updateForm.value.integrationMode,
          businessName: updateForm.value.businessName,
          ntn: formatNtnForSubmission(updateForm.value.ntn),
          cnic: updateForm.value.cnic,
          strn: updateForm.value.strn || '',
          province: updateForm.value.province,
          address: updateForm.value.city,
        };

        // Use the Save API endpoint for updating business
        const response = await APIService.post('Business/Save', updatePayload, {
          params: { userID: userID },
        });

        if (response?.data?.status === 'success' || response?.data) {
          toast({
            component: ToastificationContent,
            props: {
              title: 'Success',
              icon: 'CheckIcon',
              variant: 'success',
              text: 'Business details updated successfully',
            },
          });

          // Reload business details
          await loadBusinessDetails();
          closeUpdateModal();
        } else {
          throw new Error(response?.data?.message || 'Failed to update business details');
        }
      } catch (error) {
        console.error('Error updating business:', error);
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text:
              error.response?.data?.message || error.message || 'Failed to update business details',
          },
        });
      } finally {
        isUpdating.value = false;
      }
    };

    // Retry loading
    const retryLoading = () => {
      loadBusinessDetails();
    };

    // Utility functions
    const formatDate = (dateString) => {
      if (!dateString) return 'Not available';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      } catch (error) {
        return 'Invalid date';
      }
    };

    const catchUpdateBusiness = (error) => {
      console.log('=== catchUpdateBusiness called');
      console.log('=== error:', error);
    };

    // Handle NTN input with dynamic formatting
    const handleNtnInput = (value) => {
      // Clean the input value
      let cleanValue = value.replace(/[^A-Za-z0-9-]/g, '');

      if (cleanValue.length > 0) {
        // If starts with alphabet
        if (/^[A-Za-z]/.test(cleanValue)) {
          // Format: F123456-7 (letter + 6 digits + dash + 1 digit)
          const letterPart = cleanValue.substring(0, 1).toUpperCase();
          const numberPart = cleanValue.substring(1).replace(/[^0-9-]/g, '');

          if (numberPart.includes('-')) {
            // Handle existing dash, ensure proper format
            const parts = numberPart.split('-');
            if (parts.length === 2 && parts[0].length <= 6 && parts[1].length <= 1) {
              cleanValue = letterPart + parts[0] + '-' + parts[1];
            } else {
              // Remove dash and reformat
              const digitsOnly = numberPart.replace('-', '');
              if (digitsOnly.length >= 7) {
                cleanValue =
                  letterPart + digitsOnly.substring(0, 6) + '-' + digitsOnly.substring(6, 7);
              } else {
                cleanValue = letterPart + digitsOnly;
              }
            }
          } else {
            // No dash present
            if (numberPart.length >= 7) {
              // Auto-add dash: A123456-7
              cleanValue =
                letterPart + numberPart.substring(0, 6) + '-' + numberPart.substring(6, 7);
            } else if (numberPart.length <= 6) {
              // Still typing, don't add dash yet
              cleanValue = letterPart + numberPart;
            } else {
              // Limit to 7 digits max
              cleanValue = letterPart + numberPart.substring(0, 7);
            }
          }

          // Ensure we don't exceed the maximum length (A123456-7 = 9 chars)
          if (cleanValue.length > 9) {
            cleanValue = cleanValue.substring(0, 9);
          }
        } else {
          // If starts with digit, limit to 7 digits only
          cleanValue = cleanValue.replace(/[^0-9]/g, '').substring(0, 7);
        }
      }

      updateForm.value.ntn = cleanValue;
    };

    // Handle NTN blur to ensure proper formatting
    const handleNtnBlur = () => {
      updateForm.value.ntn = formatNtnForSubmission(updateForm.value.ntn);
    };

    // Format NTN before submission
    const formatNtnForSubmission = (ntn) => {
      if (!ntn || ntn.trim().length === 0) return ntn;

      const cleanNtn = ntn.trim();

      // If starts with letter and has 7+ digits after letter
      if (/^[A-Za-z]/.test(cleanNtn)) {
        const letter = cleanNtn.substring(0, 1).toUpperCase();
        const digits = cleanNtn.substring(1).replace(/[^0-9]/g, '');

        // If we have exactly 7 digits, format as A123456-7
        if (digits.length === 7) {
          return letter + digits.substring(0, 6) + '-' + digits.substring(6);
        }
        // If we have 6 digits, it's incomplete - return as is
        else if (digits.length === 6) {
          return letter + digits;
        }
        // If already properly formatted, return as is
        else if (/^[A-Za-z]\d{6}-\d{1}$/.test(cleanNtn)) {
          return cleanNtn.toUpperCase();
        }
      }

      return cleanNtn;
    };

    return {
      isLoading,
      businessDetails,
      showUpdateModal,
      isUpdating,
      updateForm,
      integrationModeOptions,
      provinceOptions,
      cityOptions,
      province,
      city,
      ntnMask,
      validateBusinessName,
      validateNtn,
      validateCity,
      validateAddress,
      validateCnic,
      validateIntegrationMode,
      validateStagingToken,
      validateLiveToken,
      isFormValid,
      hasNtn,
      hasCnic,
      openUpdateModal,
      closeUpdateModal,
      handleUpdateBusiness,
      retryLoading,
      formatDate,
      catchUpdateBusiness,
      handleNtnInput,
      handleNtnBlur,
      openWatchVideo,
    };
  },
};
</script>

<style lang="scss" scoped>
.dashboard-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;

  .loading-spinner {
    text-align: center;

    .spinner {
      width: 60px;
      height: 60px;
      border: 4px solid #f3f3f3;
      border-top: 4px solid #7367f0;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 20px;
    }

    .loading-text {
      color: #6e6b7b;
      font-size: 18px;
      margin: 0;
    }
  }
}

.dashboard-content {
  padding: 2rem;
}

.dashboard-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;

  .header-content {
    display: flex;
    align-items: center;
    gap: 2rem;

    .business-logo {
      .logo-image {
        width: 100px;
        aspect-ratio: 1/1;
        border-radius: 16px;
        object-fit: contain;
        background-color: white;
        border: 3px solid rgba(255, 255, 255, 0.2);
      }

      .logo-placeholder {
        width: 80px;
        height: 80px;
        border-radius: 16px;
        background: rgba(255, 255, 255, 0.1);
        display: flex;
        align-items: center;
        justify-content: center;
        border: 3px solid rgba(255, 255, 255, 0.2);
        color: rgba(255, 255, 255, 0.8);
      }
    }

    .business-info {
      flex: 1;

      .business-name {
        font-size: 2.5rem;
        font-weight: 700;
        margin: 0 0 0.5rem 0;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        color: white;
      }

      .business-tagline {
        font-size: 1.2rem;
        margin: 0 0 1rem 0;
        opacity: 0.9;
      }

      .business-meta {
        display: flex;
        gap: 1.5rem;

        .meta-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.9rem;
          opacity: 0.8;
        }
      }
    }

    .header-actions {
      .update-btn {
        background: rgba(255, 255, 255, 0.2);
        border: 2px solid rgba(255, 255, 255, 0.3);
        backdrop-filter: blur(10px);
        transition: all 0.3s ease;

        &:hover {
          background: rgba(255, 255, 255, 0.3);
          border-color: rgba(255, 255, 255, 0.5);
          transform: translateY(-2px);
        }
      }
    }
  }
}

.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.info-card {
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  }

  .card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1.5rem 1.5rem 0;
    border: none;
    background: none;

    .card-icon {
      color: #7367f0;
    }

    .card-title {
      margin: 0;
      font-weight: 600;
      color: #5e5873;
    }
  }

  .card-body {
    padding: 1.5rem;
  }
}

.contact-card {
  .contact-details {
    .contact-item {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem 0;
      border-bottom: 1px solid #f8f8f8;

      &:last-child {
        border-bottom: none;
      }

      .contact-icon {
        color: #7367f0;
        flex-shrink: 0;
      }

      .contact-text {
        flex: 1;

        label {
          display: block;
          font-size: 0.875rem;
          color: #6e6b7b;
          margin-bottom: 0.25rem;
          font-weight: 500;
        }

        span {
          color: #5e5873;
          font-weight: 500;
        }
      }
    }
  }
}

.business-card,
.address-card,
.system-card {
  .business-details,
  .address-details,
  .system-details {
    .detail-item,
    .address-item {
      padding: 1rem 0;
      border-bottom: 1px solid #f8f8f8;

      &:last-child {
        border-bottom: none;
      }

      label {
        display: block;
        font-size: 0.875rem;
        color: #6e6b7b;
        margin-bottom: 0.5rem;
        font-weight: 500;
      }

      span {
        color: #5e5873;
        font-weight: 500;
      }
    }
  }
}

.dashboard-error {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;

  .error-content {
    text-align: center;

    .error-icon {
      color: #ea5455;
      margin-bottom: 1rem;
    }

    h3 {
      color: #5e5873;
      margin-bottom: 1rem;
    }

    p {
      color: #6e6b7b;
      margin-bottom: 1.5rem;
    }
  }
}

.update-modal {
  .modal-loading {
    text-align: center;
    padding: 2rem;

    .loading-spinner {
      .spinner {
        width: 40px;
        height: 40px;
        border: 3px solid #f3f3f3;
        border-top: 3px solid #7367f0;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        margin: 0 auto 1rem;
      }

      p {
        color: #6e6b7b;
        margin: 0;
      }
    }
  }

  .update-form {
    .modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 1rem;
      margin-top: 2rem;
      padding-top: 1.5rem;
      border-top: 1px solid #f8f8f8;
    }
  }
}

// Animations
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.spinning {
  animation: spin 1s linear infinite;
}

// Responsive design
@media (max-width: 768px) {
  .dashboard-content {
    padding: 1rem;
  }

  .dashboard-header {
    padding: 1.5rem;

    .header-content {
      flex-direction: column;
      text-align: center;
      gap: 1.5rem;

      .business-info {
        .business-name {
          font-size: 2rem;
        }
      }
    }
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .info-card {
    .card-header {
      padding: 1rem 1rem 0;
    }

    .card-body {
      padding: 1rem;
    }
  }
}

@media (max-width: 480px) {
  .dashboard-header {
    .header-content {
      .business-info {
        .business-name {
          font-size: 1.75rem;
        }

        .business-meta {
          flex-direction: column;
          gap: 0.75rem;
        }
      }
    }
  }
}
</style>
